import React, { useState, useContext } from "react";
import {
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  Typography,
  TextField,
  Alert,
  InputAdornment,
  IconButton,
  OutlinedInput,
  InputLabel,
  FormControl,
  Paper,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AuthContext } from "../components/authentification/AuthContext";
import { confirmPasswordReset } from "../utils/api/api";
import Navbar from "../components/main/Navbar";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await confirmPasswordReset(uid, token, newPassword);
      setMessage(t("password-updated") || "Mot de passe mis à jour !");
      setError("");
    } catch (err) {
      setError(t("invalid-link") || "Lien invalide ou expiré.");
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault();

  return (
    <div className="w-full relative bg-primary-contrast overflow-hidden flex flex-col items-center justify-start min-h-screen">
      <Navbar />
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Paper elevation={3} sx={{ p: 4, mt: 12, borderRadius: "16px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
              <LockResetIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              {t("reset-password") || "Réinitialiser le mot de passe"}
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: "100%" }}>
              <FormControl variant="outlined" fullWidth required sx={{ mb: 2 }}>
                <InputLabel htmlFor="new-password">{t("new-password") || "Nouveau mot de passe"}</InputLabel>
                <OutlinedInput
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t("new-password") || "Nouveau mot de passe"}
                />
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, borderRadius: "12px", py: 1.5 }}
              >
                {t("reset") || "Réinitialiser"}
              </Button>

              {message && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: "8px" }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default ResetPasswordPage;
